import { Ref, ref } from 'vue';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { TKDService } from '@/models/installationPoint/TKDService';
import { Device, DeviceClasses, DeviceFactory } from '@/models/devices/Device';
import { Accessory, BatteryPack } from '@/models/installationPoint/Accessory';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';
import {
  DeviceServiceTypeEnum,
  AdapterServiceTypeEnum,
  AccessoryType,
  DeviceTypeEnum,
  BatteryExchangeReasonEnum,
} from '@/enums/device';
import { useOrderStore } from '@/store/order/orderStore';

export function useTKDService(
  installationPoint: Ref<InstallationPoint | undefined>
) {
  const newTKDService = ref<TKDService>(new TKDService());
  const newDevice = ref<DeviceClasses>();
  const newBatteryPack = ref<BatteryPack>(new BatteryPack());
  const accessoryServiceList = ref<Record<string, AccessoryService>>({});

  const initService = () => {
    if (!installationPoint.value) {
      throw new Error('No installation point');
    }

    if (
      installationPoint.value.services &&
      useOrderStore().hasTKDService(installationPoint.value?.id)
    ) {
      newTKDService.value = installationPoint.value.services.sort(
        (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
      )[0];
    }

    initNewDevice();
    initAccessoryServiceList();
  };

  const initNewDevice = () => {
    if (!installationPoint.value) {
      throw new Error('No installation point');
    }

    newDevice.value = DeviceFactory.createDevice(
      DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT
    );
    newDevice.value.setInstallationPointId(installationPoint.value.id);
  };

  const initAccessoryServiceList = () => {
    if (!installationPoint.value) return;

    const accessoryEvents = [];
    const accessoryList = installationPoint.value?.accessories.filter(
      (accessory) =>
        accessory.type !== AccessoryType.SRT_BATTERY_PACK &&
        !accessory.unmountedAt
    );

    accessoryList.forEach((accessory) => {
      const existingService = accessoryEvents.find(
        (event) => event.accessoryId === accessory.id
      );
      accessoryServiceList.value[accessory.id] =
        existingService || new AccessoryService().setAccessoryId(accessory.id);
    });
  };

  const handleSubmit = () => {
    validateInputs();
    const emitsList = [];

    handleAccessories();
    if (
      newTKDService.value &&
      installationPoint.value &&
      installationPoint.value.services
    ) {
      installationPoint.value.services.push(newTKDService.value);
      emitsList.push(installationPoint.value);
    }

    const oldDevice = installationPoint.value?.getActiveDevice();

    if (oldDevice instanceof Device) {
      if (
        newTKDService.value.deviceService &&
        newTKDService.value.deviceService.type === DeviceServiceTypeEnum.UNMOUNT
      ) {
        handleDeviceUnmount(oldDevice);
      }

      if (
        newTKDService.value.deviceService?.type ===
          DeviceServiceTypeEnum.EXCHANGE
      ) {
        handleDeviceExchange(oldDevice);
        emitsList.push(newDevice.value);
      }
      configureService(oldDevice.id);
    }

    return emitsList;
  };

  const validateInputs = (): void => {
    if (!installationPoint.value) {
      throw new Error('No installationPoint');
    }
  };

  const handleDeviceExchange = (activeDevice: DeviceClasses): void => {
    if (
      newDevice.value &&
      activeDevice &&
      newTKDService.value.deviceService &&
      installationPoint.value
    ) {
      installationPoint.value.unmountDevice(
        activeDevice.id,
        new Date(Date.now() - 1000).toISOString(),
        undefined,
        'counterPressure' in activeDevice
          ? activeDevice.counterPressure
          : undefined,
        useOrderStore().isInstallationOrder()
          ? 'mass exchange'
          : 'device exchange'
      );

      installationPoint.value.mountDevice(
        newDevice.value.id,
        new Date().toISOString(),
        undefined,
        'counterPressure' in newDevice.value
          ? newDevice.value.counterPressure
          : undefined
      );

      newTKDService.value.deviceService.newDeviceId = newDevice.value.id;

      if (!newBatteryPack.value.serialNumber) {
        throw new Error("Can't have SRT exchange without battery Pack");
      }

      const currentBatteryPack =
        installationPoint.value.getCurrentBatteryPack();

      installationPoint.value?.exchangeBatteryPack(newBatteryPack.value);

      const batteryPackAccessoryService = new AccessoryService();
      batteryPackAccessoryService
        .setAccessoryId(currentBatteryPack?.id)
        .setReason(BatteryExchangeReasonEnum.BATTERY_EXCHANGE)
        .setType(AdapterServiceTypeEnum.EXCHANGE)
        .setNewAccessoryId(newBatteryPack.value.id);

      newTKDService.value.accessoryServices.push(batteryPackAccessoryService);
    }
  };

  const handleDeviceUnmount = (oldDevice: DeviceClasses): void => {
    installationPoint.value?.unmountDevice(
      oldDevice.id,
      new Date().toISOString(),
      undefined,
      'counterPressure' in oldDevice ? oldDevice.counterPressure : undefined
    );

    const currentBatteryPack = installationPoint.value?.getCurrentBatteryPack();
    if (currentBatteryPack) {
      installationPoint.value?.demountAccessory(currentBatteryPack?.id);
    }
  };

  const handleAccessories = (): void => {
    installationPoint.value?.accessories.forEach((accessory: Accessory) => {
      if (!accessory.isAdapter()) {
        return;
      }

      if (!accessoryServiceList.value[accessory.id]?.type) {
        delete accessoryServiceList.value[accessory.id];
        return;
      }

      if (!accessoryServiceList.value[accessory.id]) {
        const mountedAccessory = new AccessoryService();
        mountedAccessory.type = AdapterServiceTypeEnum.MOUNT;
        mountedAccessory.newAccessoryId = accessory.id;
        accessoryServiceList.value[accessory.id] = mountedAccessory;
        return;
      }

      const accessoryService = accessoryServiceList.value[accessory.id];

      if (accessoryService.type === AdapterServiceTypeEnum.EXCHANGE) {
        installationPoint.value?.demountAccessory(accessory.id);
      }

      if (accessoryService.type === AdapterServiceTypeEnum.DEMOUNT) {
        installationPoint.value?.demountAccessory(accessory.id);
      }
    });
  };

  const configureService = (deviceId: string): void => {
    if (newTKDService.value.deviceService) {
      newTKDService.value.deviceService.deviceId = deviceId;
    }

    newTKDService.value.accessoryServices = [
      ...newTKDService.value.accessoryServices,
      ...Object.values(accessoryServiceList.value),
    ];
  };

  return {
    initService,
    handleSubmit,
    validateInputs,
    handleDeviceExchange,
    handleDeviceUnmount,
    handleAccessories,
    configureService,
    TKDDevice: newDevice,
    TKDService: newTKDService,
    newBatteryPack,
    accessoryServiceList,
  };
}
